import React from "react";
import {
    FaCar,
    FaPlane,
    FaRobot,
    FaShip,
    FaTractor,
    FaArrowRight
} from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import SEO from "../components/SEO";

const Applications = () => {
    const { t } = useTranslation();

    // Colors for consistent theming
    const primaryTextColor = "text-[#212529]"; // Dark text
    const secondaryTextColor = "text-[#212529]/80"; // Medium gray
    const tertiaryTextColor = "text-[#212529]/60"; // Light gray

    const [heroRef, heroInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [automotiveRef, automotiveInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    const [aerospaceRef, aerospaceInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    const [roboticsRef, roboticsInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    const [marineRef, marineInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    const [offHighwayRef, offHighwayInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    const [ctaRef, ctaInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    return (
        <div className="min-h-screen font-sans overflow-hidden relative">
            <SEO 
                title="Applications - Axialis Electric Motors"
                description="Axialis axial flux motors power automotive, aerospace, robotics, marine, and off-highway industries with superior thermal management and performance."
                keywords="axial flux applications, automotive motors, electric aviation, EV traction, marine propulsion, robotics motors, heavy duty electric vehicles"
                path="/applications"
            />
            {/* Hero Section */}
            <section
                ref={heroRef}
                className="relative pt-32 pb-8"
            >
                <div className="container mx-auto px-4">
                    <div
                        className={`max-w-3xl mx-auto text-center relative z-20 transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                    >
                        <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${primaryTextColor}`}>
                            Applications
                        </h1>
                        <div className="h-0.5 w-16 bg-primary mx-auto mb-6"></div>
                        <p className={`text-lg md:text-xl ${secondaryTextColor} mb-10 leading-relaxed`}>
                            Our innovative axial flux motors power diverse industries with superior thermal management and exceptional performance.
                        </p>
                    </div>
                </div>
            </section>

            {/* Automotive Section */}
            <section
                ref={automotiveRef}
                className="py-16"
            >
                <div className="container mx-auto px-4">
                    <div className={`flex flex-col md:flex-row items-center max-w-6xl mx-auto gap-8 transition-all duration-1000 transform ${automotiveInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <div className="w-full md:w-1/4 flex justify-center">
                            <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
                                <FaCar className="text-primary text-5xl" />
                            </div>
                        </div>
                        <div className="w-full md:w-3/4">
                            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                                <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Automotive</h2>
                                <p className={`${secondaryTextColor} leading-relaxed text-lg`}>
                                    Direct wire cooling enables sustained high performance in compact environments, making our axial flux motors ideal for traction systems in electric vehicles, where thermal limits often constrain power output.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Aerospace Section */}
            <section
                ref={aerospaceRef}
                className="py-16"
            >
                <div className="container mx-auto px-4">
                    <div className={`flex flex-col md:flex-row-reverse items-center max-w-6xl mx-auto gap-8 transition-all duration-1000 transform ${aerospaceInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <div className="w-full md:w-1/4 flex justify-center">
                            <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
                                <FaPlane className="text-primary text-5xl" />
                            </div>
                        </div>
                        <div className="w-full md:w-3/4">
                            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                                <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Aerospace & UAVs</h2>
                                <p className={`${secondaryTextColor} leading-relaxed text-lg`}>
                                    Weight savings and continuous power delivery are critical in aerospace. Our liquid-cooled windings allow for exceptional thermal control, enabling high efficiency and torque density in demanding flight applications.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Robotics Section */}
            <section
                ref={roboticsRef}
                className="py-16"
            >
                <div className="container mx-auto px-4">
                    <div className={`flex flex-col md:flex-row items-center max-w-6xl mx-auto gap-8 transition-all duration-1000 transform ${roboticsInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <div className="w-full md:w-1/4 flex justify-center">
                            <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
                                <FaRobot className="text-primary text-5xl" />
                            </div>
                        </div>
                        <div className="w-full md:w-3/4">
                            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                                <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>High-Performance Robotics</h2>
                                <p className={`${secondaryTextColor} leading-relaxed text-lg`}>
                                    In dynamic robotic systems with frequent acceleration and deceleration cycles, direct cooling ensures thermal stability and precise control under heavy loads and continuous operation.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Marine Section */}
            <section
                ref={marineRef}
                className="py-16"
            >
                <div className="container mx-auto px-4">
                    <div className={`flex flex-col md:flex-row-reverse items-center max-w-6xl mx-auto gap-8 transition-all duration-1000 transform ${marineInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <div className="w-full md:w-1/4 flex justify-center">
                            <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
                                <FaShip className="text-primary text-5xl" />
                            </div>
                        </div>
                        <div className="w-full md:w-3/4">
                            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                                <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Marine Propulsion</h2>
                                <p className={`${secondaryTextColor} leading-relaxed text-lg`}>
                                    Marine applications demand quiet, efficient, and reliable systems. Our direct cooling approach prevents overheating during prolonged use, ideal for electric outboards, underwater thrusters, and autonomous surface vessels.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Off-Highway Section */}
            <section
                ref={offHighwayRef}
                className="py-16"
            >
                <div className="container mx-auto px-4">
                    <div className={`flex flex-col md:flex-row items-center max-w-6xl mx-auto gap-8 transition-all duration-1000 transform ${offHighwayInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <div className="w-full md:w-1/4 flex justify-center">
                            <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
                                <FaTractor className="text-primary text-5xl" />
                            </div>
                        </div>
                        <div className="w-full md:w-3/4">
                            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                                <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Off-Highway and Heavy-Duty Vehicles</h2>
                                <p className={`${secondaryTextColor} leading-relaxed text-lg`}>
                                    For electric drivetrains in construction and agricultural machinery, direct wire cooling enables continuous high torque output without the risk of thermal derating, even in harsh environments.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action Section */}
            <section
                ref={ctaRef}
                className="py-20"
            >
                <div className="container mx-auto px-4 text-center">
                    <div
                        className={`max-w-3xl mx-auto transition-all duration-1000 transform ${ctaInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                    >
                        <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                            <h3 className={`text-2xl md:text-3xl font-medium mb-4 ${primaryTextColor}`}>
                                Ready to Explore How Our Motors Can Power Your Industry?
                            </h3>
                            <p className={`${secondaryTextColor} mb-8`}>
                                Contact our team to discuss your specific application needs and discover our tailored solutions.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <Link
                                    to="/contact"
                                    className="bg-primary/90 hover:bg-primary text-white backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                                >
                                    Contact Us
                                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                                </Link>
                                <Link
                                    to="/explore-motor"
                                    className="bg-white/70 hover:bg-white/80 text-[#212529] backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                                >
                                    Explore Our Motor
                                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Applications;